import clsx from "clsx";
import React, { FC, MouseEventHandler, RefObject, useCallback, useMemo, useRef } from "react";
import { useClickAway } from "react-use";

import { FormField } from "../form-field/form-field";
import { FormFieldContainer } from "../form-field/form-field-container";
import { FormFieldLabel } from "../form-field/form-field-label";
import { FormFieldMessage } from "../form-field/form-field-message";
import { colorScheme } from "../form-field/styles";
import { FormFieldColors, FormFieldStatus } from "../form-field/types";
import { TextInputDecoration, TextInputDecorationProps } from "./text-input-decoration";
import { TextInputDropdown } from "./text-input-dropdown";
import { TextInputProps } from "./types";
import { useTextInput } from "./use-text-input";

export const TextInput: FC<TextInputProps> = ({
  color = FormFieldColors.Light,
  type = "text",
  inputProps: { className: parentInputClassName, ...otherInputProps } = {},
  className: containerClassName,
  dropdownClassName: parentDropdownClassName,
  onChangeValue,
  getOptionClassName,
  options,
  value,
  defaultValue,
  transformOptions,
  mode = "onChange",
  clearable = true,
  getOptionsEmptyState,
  message,
  status = FormFieldStatus.Info,
  required,
  placeholder,
  label,
  labelDecoration,
  optionalLabel,
  collapseMessage = true,
  endDecoration,
  onClickDecoration,
  info,
  ...rest
}) => {
  const rootRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { disabled } = rest;

  const { text, setText, showOptions, setShowOptions, transformedOptions, isLoading } =
    useTextInput({
      options,
      value,
      defaultValue,
      transformOptions,
      mode,
    });

  const { dropdownClassName } = colorScheme[color];

  // set empty state (custom ReactNode or default string)
  // (please remember ReactNode could also be a string!)
  const emptyState = useMemo(() => {
    if (!(options ?? transformedOptions) || !text) return null;
    if (getOptionsEmptyState) return getOptionsEmptyState(text);
    return `No options for "${text}"`;
  }, [getOptionsEmptyState, options, text, transformedOptions]);

  // Close dropdown on click outside
  useClickAway(rootRef, () => setShowOptions(false));

  const toggleDropdown: MouseEventHandler = useCallback(
    (e) => {
      if (!disabled) {
        e.stopPropagation();
        inputRef.current?.focus();
        setShowOptions((v) => !v);
      }
    },
    [setShowOptions, disabled]
  );

  const decorationType: TextInputDecorationProps["type"] | null = isLoading
    ? "spinner"
    : mode === "select"
    ? "chevron-down"
    : clearable && text
    ? "clear"
    : type === "search"
    ? "search"
    : endDecoration ?? null;

  const optionsToShow = (transformOptions ? transformedOptions : options) ?? [];

  return (
    <FormFieldContainer
      disabled={disabled}
      ref={rootRef}
      className={clsx("inline-block", containerClassName)}
      {...rest}
    >
      <FormFieldLabel
        required={required}
        label={label}
        info={info}
        labelDecoration={labelDecoration}
        optionalLabel={optionalLabel}
      />
      <div className="relative">
        <FormField
          color={color}
          className={clsx("h-11", parentInputClassName)}
          hasValue={!!text}
          disabled={disabled}
        >
          {/* Text input */}
          <input
            ref={inputRef}
            type="text"
            required={required}
            {...otherInputProps}
            readOnly={mode === "select"}
            disabled={disabled}
            onChange={(e) => {
              const v = e.target.value;
              if (mode !== "select") setText(v);
              if (mode === "onChange") onChangeValue?.(v);
            }}
            onBlur={(e) => {
              const v = e.target.value;
              if (mode === "onBlur") onChangeValue?.(v);
            }}
            value={text}
            placeholder={placeholder}
            onClick={toggleDropdown}
            className={clsx("pl-3 py-1", mode === "select" && !disabled && "cursor-pointer")}
          />
          {/* End decoration */}
          <span
            className="h-full w-8 cursor-text"
            onClick={
              decorationType === null
                ? () => {
                    inputRef.current?.focus();
                    inputRef.current?.click();
                  }
                : undefined
            }
          >
            {decorationType !== null && (
              <TextInputDecoration
                type={decorationType}
                disabled={disabled}
                onClickClear={(e) => {
                  e.stopPropagation();
                  setText("");
                  onChangeValue?.("");
                }}
                onClickIcon={onClickDecoration}
                onClickChevronDown={toggleDropdown}
              />
            )}
          </span>
        </FormField>
        {/* Options dropdown */}
        {optionsToShow.length > 0 && !disabled && (
          <TextInputDropdown
            color={color}
            currentValue={text}
            className={clsx(!showOptions && "hidden", dropdownClassName, parentDropdownClassName)}
            isOpen={showOptions}
            options={optionsToShow}
            disabled={disabled}
            onClickOption={(option) => {
              onChangeValue?.(option.label, option); // trigger onChangeValue for parent element
              setText(option.label); // align internal text value
              setShowOptions(false); // close dropdown
            }}
            getOptionClassName={getOptionClassName}
            emptyState={emptyState}
          />
        )}
      </div>

      {(message || !collapseMessage) && <FormFieldMessage message={message} status={status} />}
    </FormFieldContainer>
  );
};
